import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  // Initialisering: les fra localStorage og verifiser mot backend
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');

    if (!savedToken) {
      setLoading(false);
      return;
    }

    // Sett umiddelbart fra cache (unngår blank skjerm)
    setToken(savedToken);
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch { /* ugyldig JSON */ }
    }

    // Verifiser mot backend i bakgrunnen
    api.get('/auth/me')
      .then((res) => {
        if (res.data.success && res.data.user) {
          setUser(res.data.user);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        } else {
          logout();
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        // Kun logg ut ved autentiseringsfeil (401/403). Ved 5xx / nettverksfeil
        // (server nede) beholder vi token og brukeren prøver igjen automatisk.
        if (status === 401 || status === 403) {
          logout();
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((accessToken, userData) => {
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    user,
    token,
    isAdmin,
    isAuthenticated: !!token && !!user,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must brukes innenfor <AuthProvider>');
  return ctx;
}

export default AuthContext;
